/**
 * Google Analytics 4 (GA4) integration.
 *
 * Loads gtag.js lazily, only when `VITE_GA_MEASUREMENT_ID` is set. Tracks SPA
 * page views via `trackPageView`, called from a useEffect that listens to React
 * Router location changes (see `AnalyticsTracker` in `App.tsx`).
 *
 * Behavior matrix:
 *  - env var unset (any environment): no script, no globals, no requests — silent.
 *  - env var set: gtag.js script appended to <head> on first init, automatic page
 *    view disabled (we fire manually on route change so SPA navigation is captured).
 *
 * Production deployment requires `VITE_GA_MEASUREMENT_ID=G-XXXXXXXXXX` to be set
 * during the website's build. UAT and dev intentionally leave it unset.
 *
 * Cookie consent / GDPR / CCPA: deferred. Add a banner before targeting
 * EU/UK traffic at scale.
 */

import { maskTokenUrl } from './maskTokenUrl';

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

let initialized = false;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export function isAnalyticsEnabled(): boolean {
  return typeof MEASUREMENT_ID === 'string' && MEASUREMENT_ID.length > 0;
}

export function initAnalytics(): void {
  if (initialized || !isAnalyticsEnabled()) return;
  initialized = true;

  // Inject gtag.js loader. Uses async so it doesn't block parse / first paint.
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Bootstrap dataLayer + gtag stub. The real gtag is loaded asynchronously by
  // the script above; until then, calls accumulate in dataLayer and replay
  // when gtag.js finishes loading. Standard GA4 install pattern.
  window.dataLayer = window.dataLayer || [];
  // IMPORTANT: must push the native `arguments` object, NOT a rest-param array.
  // gtag.js's internal command processor type-checks dataLayer entries via
  // Object.prototype.toString.call() and silently skips [object Array] entries —
  // only [object Arguments] and plain objects are processed. Using `...args`
  // (which creates an Array) causes gtag to ignore our js/config/event commands,
  // so the GA4 tracker never initializes and no g/collect requests fire.
  // eslint-disable-next-line prefer-rest-params, prefer-spread
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  // Disable automatic page_view; we fire manually on route change so SPA
  // navigation registers correctly (default would only fire on hard load).
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
  });
}

export function trackPageView(path: string, title?: string): void {
  if (!initialized || !isAnalyticsEnabled()) return;

  // Token-bearing paths (/invite/<token>, /reset-password/<token>, …) are masked so the secret
  // never reaches Google Analytics — recruitment workflow step 5c.
  window.gtag('event', 'page_view', {
    page_path: maskTokenUrl(path),
    page_title: title ?? document.title,
    page_location: maskTokenUrl(window.location.href),
  });
}
